/**
 * Send history (#2575). `test_send` rows are marked so a rehearsal
 * cannot be read as a real alert. Times render in the browser timezone.
 */
import type { CodexCapacityAlertHistoryEntry } from '@nadoutong/contracts';
import { formatLocalTimestamp } from '../formatters.ts';
import { useT } from '../../i18n/index.ts';
import { Chip, Table, TableEmpty, TBody, Td, Th, THead, Tr } from '../../ui/index.ts';

interface Props {
  readonly entries: readonly CodexCapacityAlertHistoryEntry[];
  readonly limit: number;
  readonly incidentId: string | null;
}

export function CodexCapacityHistoryTable({ entries, limit, incidentId }: Props): JSX.Element {
  const t = useT();
  const capped = entries.length >= limit;
  return (
    <div className="flex flex-col gap-3">
      <p
        className="m-0 rounded border border-line-soft bg-well px-3 py-2 text-dense text-paper-mid"
        data-testid="codex-capacity-history-banner"
      >
        {t('codex_capacity.history.banner', { count: String(entries.length) })}
      </p>
      {incidentId !== null ? (
        <p className="m-0 text-dense text-paper-mid" data-testid="codex-capacity-history-filter">
          {t('codex_capacity.history.filter', { id: incidentId })}
        </p>
      ) : null}

      <Table testId="codex-capacity-history-table">
        <THead>
          <Tr interactive={false}>
            <Th>{t('codex_capacity.history.column.sent_at')}</Th>
            <Th>{t('codex_capacity.history.column.account')}</Th>
            <Th>{t('codex_capacity.history.column.kind')}</Th>
            <Th num>{t('codex_capacity.history.column.window')}</Th>
            <Th>{t('codex_capacity.history.column.incident')}</Th>
          </Tr>
        </THead>
        <TBody>
          {entries.length === 0 ? (
            <TableEmpty colSpan={5}>{t('codex_capacity.history.empty')}</TableEmpty>
          ) : (
            entries.map((entry) => (
              <Tr
                key={entry.id}
                interactive={false}
                data-testid={`codex-capacity-history-row-${entry.id}`}
                className={entry.test_send ? 'bg-signal-dim/40' : undefined}
              >
                <Td mono>{formatLocalTimestamp(entry.sent_at, { seconds: true })}</Td>
                <Td emphasis>
                  <span className="flex flex-col">
                    <span>{entry.to_address}</span>
                    <span className="font-mono text-micro text-paper-low">{entry.account_id}</span>
                  </span>
                </Td>
                <Td>
                  <span className="flex flex-wrap items-center gap-1.5">
                    <Chip
                      tone={entry.kind === 'elevated' ? 'bad' : 'ok'}
                      lamp={false}
                      testId={`codex-capacity-history-kind-${entry.id}`}
                    >
                      {t(
                        entry.kind === 'elevated'
                          ? 'codex_capacity.history.kind.elevated'
                          : 'codex_capacity.history.kind.recovered',
                      )}
                    </Chip>
                    {entry.test_send ? (
                      <Chip
                        tone="signal"
                        lamp={false}
                        testId={`codex-capacity-history-test-send-${entry.id}`}
                      >
                        {t('codex_capacity.history.test_send')}
                      </Chip>
                    ) : null}
                  </span>
                </Td>
                <Td num>{formatPct(entry.window_pct)}</Td>
                <Td mono>{entry.incident_id}</Td>
              </Tr>
            ))
          )}
        </TBody>
      </Table>

      {entries.length > 0 ? (
        <p className="m-0 text-dense text-paper-low" data-testid="codex-capacity-history-coverage">
          {t(
            capped
              ? 'codex_capacity.history.coverage_capped'
              : 'codex_capacity.history.coverage_complete',
            { count: String(entries.length) },
          )}
        </p>
      ) : null}
    </div>
  );
}

function formatPct(value: number): string {
  if (!Number.isFinite(value)) return '—';
  return `${value.toFixed(1)}%`;
}
